// Free, local, no-AI keyword/stem/synonym matching engine over a
// "heading + body text" shaped corpus. Originally built to search a
// site-wide scraped snapshot (a since-removed scripts/prerender.mjs step
// that wrote /content-index.json) for the chat widget — that was dropped
// because it depended on a separate, occasionally-paused build step ever
// having successfully run, so the widget could go dark in production for
// reasons with no content-related cause at all. The chat widget now
// searches the admin-managed FAQ database instead (see faqSearch.ts, a
// thin wrapper around the primitives exported below), which has no such
// dependency. This engine itself stays in use for two different shapes of
// "search this known-good text" -- faqSearch.ts's FAQ entries and
// GuestGuide.tsx's own per-booking page content — both built as a
// synthetic ContentIndex rather than from any fetched file.

export interface ContentSection {
  heading: string;
  text: string;
}

export interface ContentPage {
  url: string;
  title: string;
  intro: string;
  sections: ContentSection[];
}

export interface ContentIndex {
  generatedAt: string;
  pages: ContentPage[];
}

export interface SearchResult {
  url: string;
  pageTitle: string;
  heading: string;
  snippet: string;
  score: number;
}

const STOP_WORDS = new Set([
  'what', 'is', 'the', 'are', 'you', 'your', 'have', 'has', 'can', 'for', 'and', 'in', 'at', 'to', 'do', 'does',
  'how', 'much', 'many', 'there', 'any', 'please', 'of', 'a', 'an', 'on', 'with', 'me', 'i', 'my', 'it', 'be',
  'will', 'this', 'that', 'we', 'us', 'our', 'or', 'if', 'about',
  // An interrogative connector exactly like 'what'/'how' above, carrying no
  // topical content of its own -- without it, "where is X located" loses
  // to any heading that happens to start with "Where" rhetorically
  // ("Where Should Groups Stay in Ahmedabad?", "Where Do Your Wedding
  // Guests Stay?"), none of which are actually about the property's own
  // location/address.
  'where',
  // The brand's own name, appearing in nearly every page's title and a
  // great many headings/sentences -- pure noise for ranking *which* page
  // is relevant, since it's equally present on all of them regardless of
  // topic. Kept out of here: 'stay' on its own, which is also a genuine
  // generic hospitality word, not purely the brand name.
  'homeland',
  // Generic connector words that show up across many unrelated FAQ-style
  // headings ("Is X available?", "Do you offer X?"). Without these, a
  // question like "is wifi available" can match "Is parking available?"
  // ahead of the actual wifi content, just because both share "available".
  'available', 'provide', 'provided', 'offer', 'offered', 'allowed', 'include', 'included', 'need', 'needed',
  'yes', 'no', 'get',
  // Same idea, specifically for "can I bring my X" / "do you allow X" style
  // questions -- "bring" especially is the literal first word of multiple
  // unrelated blog headings ("Bringing the whole family for Diwali?",
  // "...Bringing Business Travellers to Ahmedabad"), which otherwise won
  // outright over the actually-relevant page just by sharing that one verb.
  'bring', 'bringing', 'allow',
]);

// A handful of genuinely common hospitality abbreviations people actually
// type, expanded on the query side only — the content itself never needs
// editing to match them.
const QUERY_SYNONYMS: Record<string, string[]> = {
  ac: ['air', 'condition'],
  wifi: ['internet', 'wi', 'fi'],
  bnb: ['bed', 'breakfast'],
  // The site's own pets policy only ever says "pets" -- it never says "dog"
  // or "cat" -- so without this, those genuinely common phrasings share
  // zero words with the actual answer and the matcher has nothing to work
  // with at all.
  dog: ['pet'],
  dogs: ['pet'],
  cat: ['pet'],
  cats: ['pet'],
  puppy: ['pet'],
  puppies: ['pet'],
  kitten: ['pet'],
  kittens: ['pet'],
  animal: ['pet'],
  animals: ['pet'],
  // "Lift" (the common non-US phrasing) never appears verbatim in content
  // that only ever says "elevator".
  lift: ['elevator'],
  // "How many people can stay" shares zero words with a FAQ phrased as
  // "How many guests can stay" -- "people"/"person" are genuinely common
  // ways to ask the same thing content only ever calls "guests".
  people: ['guest'],
  person: ['guest'],
};

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOP_WORDS.has(t));
}

/** A light, dependency-free stemmer — just enough to match common variants
 * ("located" / "location", "rooms" / "room", "cooking" / "cook") without a
 * real NLP library. Deliberately conservative (only strips on longer words)
 * to avoid collapsing unrelated short words together. */
export function stem(word: string): string {
  if (word.length > 6 && word.endsWith('ations')) return `${word.slice(0, -6)}at`;
  if (word.length > 5 && word.endsWith('ation')) return `${word.slice(0, -5)}at`;
  if (word.length > 5 && word.endsWith('ing')) return word.slice(0, -3);
  if (word.length > 5 && word.endsWith('ied')) return `${word.slice(0, -3)}y`;
  if (word.length > 4 && word.endsWith('ed')) return word.slice(0, -2);
  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.length > 3 && word.endsWith('es')) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

export function expandQueryTokens(tokens: string[]): string[] {
  const expanded = [...tokens];
  for (const t of tokens) {
    if (QUERY_SYNONYMS[t]) expanded.push(...QUERY_SYNONYMS[t]);
  }
  return expanded;
}

export interface TokenSet {
  raw: string[];
  stemmed: string[];
}

export function tokenSet(text: string): TokenSet {
  const raw = tokenize(text);
  return { raw, stemmed: raw.map(stem) };
}

/** Exact match scores highest, a stemmed match (plural/tense variants) a
 * little less, and a loose prefix match (word roots, typos, "checkin" vs
 * "check-in") least. Three things keep this from firing on coincidence:
 * - Gated to tokens of length >= 4 on both sides for the *prefix* tier —
 *   short tokens like "wi"/"fi"/"ac" (abbreviations already handled via
 *   QUERY_SYNONYMS) otherwise turn up as substrings of unrelated words
 *   (e.g. "wi" inside "swimming"), which matched a wifi FAQ section to "is
 *   there a swimming pool?".
 * - A short (<= 3 char) token never earns full "exact" strength even when
 *   it IS a literal whole-word hit, for the same reason — a word that
 *   short is often part of something else entirely ("pet" inside the
 *   Statue of Unity blog's "Pet Zone" theme-park attraction) outscored the
 *   real pets-policy page, which only *stems* to "pet" (from "pets") and
 *   so lost to a coincidental exact hit elsewhere. Demoted to the same
 *   weight as a prefix match rather than dropped entirely — it's still
 *   real signal, just not strong enough to win outright on its own.
 * - Prefix (startsWith), not "appears anywhere in" (includes) — real
 *   variants are almost always a shared root at the START of the word
 *   ("checkin"/"check", "cancel"/"cancellation"). `includes` anywhere also
 *   matches a real short word buried in the MIDDLE of an unrelated query
 *   word — "nonsense" contains the real word "sense" — which otherwise
 *   turned pure gibberish queries into confident (wrong) answers. */
export function matchScore(queryToken: string, queryStem: string, set: TokenSet, exact: number, stemmed: number, partial: number): number {
  if (set.raw.includes(queryToken)) return queryToken.length <= 3 ? partial : exact;
  if (set.stemmed.includes(queryStem)) return stemmed;
  if (queryToken.length >= 4 && set.raw.some((t) => t.length >= 4 && (t.startsWith(queryToken) || queryToken.startsWith(t)))) {
    return partial;
  }
  return 0;
}

/** Inverse document frequency: a word that shows up in a handful of
 * sections is a strong, specific signal; one that shows up in dozens is
 * weak and generic — and once the index covers not just the dozen core
 * pages but ~80 SEO landing pages/blog posts too, plenty of ordinary words
 * ("where", "guide", "stay") get templated into many of those titles, and
 * would otherwise swamp a real match on a rare, on-topic word with a pile
 * of coincidental ones. Titles are deliberately excluded from the counts —
 * a page's title repeats once per section, which would wildly over-count
 * its own words relative to body text. */
export function buildIdf(candidates: { headingSet: TokenSet; textSet: TokenSet }[]): (token: string) => number {
  const docFrequency = new Map<string, number>();
  for (const c of candidates) {
    for (const t of new Set([...c.headingSet.raw, ...c.textSet.raw])) {
      docFrequency.set(t, (docFrequency.get(t) ?? 0) + 1);
    }
  }
  const total = candidates.length;
  return (token: string) => Math.log((total + 1) / ((docFrequency.get(token) ?? 0) + 1)) + 1;
}

/** A result this confident is shown as a direct answer rather than the
 * "ask the host" fallback. Calibrated against the real site's ~90-page
 * index (not a small synthetic sample) — scores are IDF-weighted now, so
 * they run much higher than a raw hit count. A genuinely on-topic match
 * tends to land at 20+ even when thin; the real FAQ page groups several
 * questions under one heading rather than one heading per question, so a
 * correct-but-diluted match (e.g. "parking" inside a multi-question "Property
 * & Location" block) can still land as low as ~6 — this stays just under
 * that, rather than forcing those into the "ask the host" fallback too. */
export const MIN_CONFIDENT_SCORE = 5;

export function searchContentIndex(index: ContentIndex, query: string, limit = 3): SearchResult[] {
  const queryTokens = expandQueryTokens(tokenize(query));
  if (queryTokens.length === 0) return [];
  const queryStems = queryTokens.map(stem);

  interface Candidate {
    url: string;
    pageTitle: string;
    heading: string;
    snippet: string;
    score: number;
    headingSet: TokenSet;
    textSet: TokenSet;
    titleSet: TokenSet;
  }

  const candidates: Candidate[] = [];
  for (const page of index.pages) {
    const titleSet = tokenSet(page.title);
    // The page's own title/intro, even when the intro is empty — a page's
    // title alone (e.g. "Cancellation Policy") must still be matchable, or a
    // page with no lead-in paragraph before its first heading can never win.
    const sections: ContentSection[] = [{ heading: page.title, text: page.intro }, ...page.sections];
    for (const section of sections) {
      const headingSet = tokenSet(section.heading);
      const textSet = tokenSet(section.text);
      if (headingSet.raw.length === 0 && textSet.raw.length === 0) continue;
      candidates.push({
        url: page.url,
        pageTitle: page.title,
        heading: section.heading,
        snippet: section.text || section.heading,
        score: 0,
        headingSet,
        textSet,
        titleSet,
      });
    }
  }

  const idf = buildIdf(candidates);

  for (const c of candidates) {
    let score = 0;
    for (let i = 0; i < queryTokens.length; i++) {
      const qt = queryTokens[i];
      const qs = queryStems[i];
      const weight = idf(qt);
      score += matchScore(qt, qs, c.headingSet, 3, 2.3, 1.5) * weight;
      score += matchScore(qt, qs, c.textSet, 1, 0.75, 0.5) * weight;
      // The page's title contributes a smaller signal to every one of its
      // sections, so a page-level topic word still counts even when the
      // specific section heading doesn't repeat it.
      score += matchScore(qt, qs, c.titleSet, 1.2, 0.9, 0) * 0.5 * weight;
    }
    c.score = score * (1 + 0.15 * Math.min(queryTokens.length, 4));
  }

  candidates.sort((a, b) => b.score - a.score);

  // At most one section per page, so the same page doesn't crowd out others.
  const seenPages = new Set<string>();
  const results: SearchResult[] = [];
  for (const c of candidates) {
    if (c.score <= 0 || seenPages.has(c.url)) continue;
    seenPages.add(c.url);
    results.push({ url: c.url, pageTitle: c.pageTitle, heading: c.heading, snippet: c.snippet, score: c.score });
    if (results.length >= limit) break;
  }
  return results;
}
